import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonText,
  Switch,
  useConfirm,
  useToast,
} from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { useState } from 'react';

import { api } from '../api/client.js';

interface TriggerField {
  key: string;
  label: string;
}
interface TriggerDef {
  event: string;
  label: string;
  fields: TriggerField[];
}
interface ActionParam {
  key: string;
  label: string;
  type: 'text' | 'select' | 'dynamic-select';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  optionsEndpoint?: string;
  optionsListKey?: string;
  optionsValueKey?: string;
  optionsLabelKey?: string;
}
interface ActionDef {
  key: string;
  label: string;
  params: ActionParam[];
}
interface Condition {
  field: string;
  op: 'equals' | 'not_equals' | 'contains' | 'gt' | 'lt';
  value: string;
}
type Frequency = 'daily' | 'weekly' | 'monthly';
interface ScheduleConfig {
  frequency: Frequency;
  hour: number;
  minute: number;
  weekday?: number;
  monthday?: number;
}
interface AutomationDto {
  id: string;
  name: string;
  triggerEvent: string;
  conditions: Condition[];
  actionKey: string;
  actionParams: Record<string, string>;
  enabled: boolean;
  triggerType: 'event' | 'schedule';
  schedule: ScheduleConfig | null;
  delayDays: number;
}
interface RunDto {
  id: string;
  status: 'ok' | 'skipped' | 'failed' | 'scheduled';
  error: string | null;
  ranAt: string;
}

const SCHEDULE_EVENT = 'core.schedule';
const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const FREQUENCY_LABELS: Record<Frequency, string> = {
  daily: 'täglich',
  weekly: 'wöchentlich',
  monthly: 'monatlich',
};

function scheduleSummary(schedule: ScheduleConfig): string {
  const time = `${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')} Uhr`;
  if (schedule.frequency === 'daily') return `täglich um ${time}`;
  if (schedule.frequency === 'weekly') {
    return `jeden ${WEEKDAYS[schedule.weekday ?? 1]} um ${time}`;
  }
  return `am ${schedule.monthday ?? 1}. des Monats um ${time}`;
}

const OP_LABELS: Record<Condition['op'], string> = {
  equals: 'ist gleich',
  not_equals: 'ist nicht',
  contains: 'enthält',
  gt: 'ist größer als',
  lt: 'ist kleiner als',
};

const RUN_BADGE: Record<
  RunDto['status'],
  { tone: 'success' | 'neutral' | 'danger' | 'accent'; label: string }
> = {
  ok: { tone: 'success', label: 'Ausgeführt' },
  skipped: { tone: 'neutral', label: 'Übersprungen' },
  failed: { tone: 'danger', label: 'Fehlgeschlagen' },
  scheduled: { tone: 'accent', label: 'Geplant' },
};

const DEFAULT_SCHEDULE: ScheduleConfig = { frequency: 'daily', hour: 8, minute: 0 };

/** Parameter-Feld des Builders — dynamic-select lädt Optionen zur Laufzeit. */
function ParamField({
  param,
  value,
  onChange,
  placeholderHint,
}: {
  param: ActionParam;
  value: string;
  onChange: (value: string) => void;
  placeholderHint: string;
}) {
  const options = useQuery({
    queryKey: ['automation-options', param.optionsEndpoint],
    enabled: param.type === 'dynamic-select' && param.optionsEndpoint !== undefined,
    retry: false,
    queryFn: () => api<Record<string, unknown>>(param.optionsEndpoint!),
  });

  if (param.type === 'text') {
    return (
      <Field label={param.label} hint={placeholderHint}>
        {(id) => (
          <Input
            id={id}
            required={param.required ?? false}
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </Field>
    );
  }

  const selectOptions =
    param.type === 'select'
      ? (param.options ?? [])
      : (
          (options.data?.[param.optionsListKey ?? 'items'] as
            Array<Record<string, string>> | undefined) ?? []
        ).map((row) => ({
          value: row[param.optionsValueKey ?? 'id'] ?? '',
          label: row[param.optionsLabelKey ?? 'name'] ?? '',
        }));

  return (
    <Field label={param.label}>
      {(id) => (
        <Select
          id={id}
          required={param.required ?? false}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">– wählen –</option>
          {selectOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/**
 * v4: Automationen (Wenn → Bedingung → Dann) — Regeln bauen, schalten,
 * Protokoll der Läufe einsehen. Trigger/Aktionen kommen aus dem Katalog,
 * den die aktivierten Module registrieren.
 */
export default function AutomationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [runsFor, setRunsFor] = useState<AutomationDto | null>(null);

  const [name, setName] = useState('');
  const [triggerEvent, setTriggerEvent] = useState('');
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [actionKey, setActionKey] = useState('');
  const [actionParams, setActionParams] = useState<Record<string, string>>({});
  const [schedule, setSchedule] = useState<ScheduleConfig>(DEFAULT_SCHEDULE);
  const [delayDays, setDelayDays] = useState(0);

  const isSchedule = triggerEvent === SCHEDULE_EVENT;

  const catalog = useQuery({
    queryKey: ['automations', 'catalog'],
    queryFn: () =>
      api<{ triggers: TriggerDef[]; actions: ActionDef[] }>('/api/v1/automations/catalog'),
  });
  const rules = useQuery({
    queryKey: ['automations', 'list'],
    queryFn: () => api<{ items: AutomationDto[] }>('/api/v1/automations'),
  });
  const runs = useQuery({
    queryKey: ['automations', 'runs', runsFor?.id],
    enabled: runsFor !== null,
    queryFn: () => api<{ items: RunDto[] }>(`/api/v1/automations/${runsFor!.id}/runs`),
  });

  const trigger = catalog.data?.triggers.find((entry) => entry.event === triggerEvent);
  const action = catalog.data?.actions.find((entry) => entry.key === actionKey);
  const triggerLabel = (event: string): string =>
    catalog.data?.triggers.find((entry) => entry.event === event)?.label ?? event;
  const actionLabel = (key: string): string =>
    catalog.data?.actions.find((entry) => entry.key === key)?.label ?? key;
  const placeholderHint =
    trigger && trigger.fields.length > 0
      ? `Platzhalter: ${trigger.fields.map((field) => `{{${field.key}}}`).join(', ')}`
      : 'Keine Platzhalter für diesen Auslöser verfügbar';

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['automations'] });
  };

  const openCreate = (): void => {
    setEditingId(null);
    setName('');
    setTriggerEvent('');
    setConditions([]);
    setActionKey('');
    setActionParams({});
    setSchedule(DEFAULT_SCHEDULE);
    setDelayDays(0);
    setBuilderOpen(true);
  };

  const openEdit = (rule: AutomationDto): void => {
    setEditingId(rule.id);
    setName(rule.name);
    setTriggerEvent(rule.triggerEvent);
    setConditions(rule.conditions ?? []);
    setActionKey(rule.actionKey);
    setActionParams(rule.actionParams ?? {});
    setSchedule(rule.schedule ?? DEFAULT_SCHEDULE);
    setDelayDays(rule.delayDays ?? 0);
    setBuilderOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const scheduleFlag = triggerEvent === SCHEDULE_EVENT;
      const body = {
        name,
        triggerEvent,
        conditions: scheduleFlag ? [] : conditions,
        actionKey,
        actionParams,
        enabled: true,
        triggerType: scheduleFlag ? 'schedule' : 'event',
        schedule: scheduleFlag ? schedule : null,
        delayDays: scheduleFlag ? 0 : delayDays,
      };
      return editingId !== null
        ? api(`/api/v1/automations/${editingId}`, { method: 'PUT', body })
        : api('/api/v1/automations', { method: 'POST', body });
    },
    onSuccess: () => {
      setBuilderOpen(false);
      toast.success(editingId !== null ? 'Automation aktualisiert' : 'Automation angelegt');
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const runNow = useMutation({
    mutationFn: (id: string) =>
      api<{ status: 'ok' | 'failed'; error: string | null }>(`/api/v1/automations/${id}/run-now`, {
        method: 'POST',
      }),
    onSuccess: (result) => {
      if (result.status === 'ok') {
        toast.success('Automation ausgeführt — Ergebnis im Protokoll.');
      } else {
        toast.error(`Ausführung fehlgeschlagen: ${result.error ?? 'unbekannt'}`);
      }
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api(`/api/v1/automations/${input.id}`, { method: 'PUT', body: { enabled: input.enabled } }),
    onSuccess: refresh,
    onError: (error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/automations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Automation gelöscht');
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const canSave =
    name.trim() !== '' &&
    triggerEvent !== '' &&
    actionKey !== '' &&
    (action?.params ?? []).every(
      (param) => !param.required || (actionParams[param.key] ?? '').trim() !== '',
    );

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page__title">Automationen</h1>
          <p className="page__lead">
            Wenn etwas passiert, tut Aurora automatisch etwas — z. B. „Wenn ein Deal gewonnen wird,
            erstelle eine Aufgabe".
          </p>
        </div>
        <Button onClick={openCreate}>Neue Automation</Button>
      </div>

      {rules.isLoading ? (
        <SkeletonText lines={5} />
      ) : (rules.data?.items.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            icon={<Zap size={28} aria-hidden />}
            title="Noch keine Automationen"
            description="Baue deine erste Wenn-Dann-Regel — sie läuft ab sofort bei jedem passenden Ereignis."
            action={<Button onClick={openCreate}>Neue Automation</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--au-space-3)' }}>
          {rules.data!.items.map((rule) => (
            <Card key={rule.id}>
              <div className="auto-rule">
                <div className="auto-rule__info">
                  <div className="auto-rule__name">{rule.name}</div>
                  <div className="auto-rule__flow">
                    <span className="auto-rule__chip auto-rule__chip--when">
                      {rule.triggerType === 'schedule' && rule.schedule
                        ? `⏰ ${scheduleSummary(rule.schedule)}`
                        : `Wenn: ${triggerLabel(rule.triggerEvent)}`}
                    </span>
                    {rule.triggerType === 'event' && rule.delayDays > 0 ? (
                      <span className="auto-rule__chip">
                        +{rule.delayDays} {rule.delayDays === 1 ? 'Tag' : 'Tage'} später
                      </span>
                    ) : null}
                    {(rule.conditions ?? []).length > 0 ? (
                      <span className="auto-rule__chip">
                        {rule.conditions.length}{' '}
                        {rule.conditions.length === 1 ? 'Bedingung' : 'Bedingungen'}
                      </span>
                    ) : null}
                    <span aria-hidden>→</span>
                    <span className="auto-rule__chip auto-rule__chip--then">
                      Dann: {actionLabel(rule.actionKey)}
                    </span>
                  </div>
                </div>
                <div className="auto-rule__actions">
                  <Switch
                    checked={rule.enabled}
                    onChange={(enabled) => toggle.mutate({ id: rule.id, enabled })}
                    aria-label={`Automation „${rule.name}" ${rule.enabled ? 'deaktivieren' : 'aktivieren'}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={runNow.isPending && runNow.variables === rule.id}
                    onClick={() => runNow.mutate(rule.id)}
                  >
                    Jetzt testen
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setRunsFor(rule)}>
                    Protokoll
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(rule)}>
                    Bearbeiten
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void confirmDialog({
                        title: `Automation „${rule.name}" löschen?`,
                        description: 'Die Regel und ihr Protokoll werden entfernt.',
                        confirmLabel: 'Löschen',
                        danger: true,
                      }).then((ok) => {
                        if (ok) remove.mutate(rule.id);
                      });
                    }}
                  >
                    Löschen
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Builder */}
      <Dialog
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        aria-label={editingId !== null ? 'Automation bearbeiten' : 'Neue Automation'}
        wide
      >
        <form
          className="au-dialog-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <h2>{editingId !== null ? 'Automation bearbeiten' : 'Neue Automation'}</h2>
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                required
                placeholder="z. B. „Onboarding-Aufgabe bei gewonnenem Deal“"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <div className="auto-section">Wenn …</div>
          <Field label="Auslöser">
            {(id) => (
              <Select
                id={id}
                required
                value={triggerEvent}
                onChange={(event) => {
                  setTriggerEvent(event.target.value);
                  setConditions([]);
                }}
              >
                <option value="">– Ereignis wählen –</option>
                {(catalog.data?.triggers ?? []).map((entry) => (
                  <option key={entry.event} value={entry.event}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {isSchedule ? (
            <div className="auto-schedule">
              <Field label="Häufigkeit">
                {(id) => (
                  <Select
                    id={id}
                    value={schedule.frequency}
                    onChange={(event) =>
                      setSchedule({ ...schedule, frequency: event.target.value as Frequency })
                    }
                  >
                    {(Object.keys(FREQUENCY_LABELS) as Frequency[]).map((freq) => (
                      <option key={freq} value={freq}>
                        {FREQUENCY_LABELS[freq]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {schedule.frequency === 'weekly' ? (
                <Field label="Wochentag">
                  {(id) => (
                    <Select
                      id={id}
                      value={String(schedule.weekday ?? 1)}
                      onChange={(event) =>
                        setSchedule({ ...schedule, weekday: Number(event.target.value) })
                      }
                    >
                      {WEEKDAYS.map((label, index) => (
                        <option key={index} value={index}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}
              {schedule.frequency === 'monthly' ? (
                <Field label="Tag im Monat" hint="Bei kürzeren Monaten am letzten Tag.">
                  {(id) => (
                    <Input
                      id={id}
                      type="number"
                      min={1}
                      max={31}
                      value={String(schedule.monthday ?? 1)}
                      onChange={(event) =>
                        setSchedule({ ...schedule, monthday: Number(event.target.value) })
                      }
                    />
                  )}
                </Field>
              ) : null}
              <Field label="Uhrzeit" hint="Ortszeit (Europe/Berlin).">
                {(id) => (
                  <Input
                    id={id}
                    type="time"
                    value={`${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`}
                    onChange={(event) => {
                      const [h, m] = event.target.value.split(':').map(Number);
                      setSchedule({ ...schedule, hour: h ?? 0, minute: m ?? 0 });
                    }}
                  />
                )}
              </Field>
            </div>
          ) : trigger && trigger.fields.length > 0 ? (
            <>
              {conditions.map((condition, index) => (
                <div key={index} className="auto-condition">
                  <Select
                    aria-label={`Bedingung ${index + 1}: Feld`}
                    value={condition.field}
                    onChange={(event) => {
                      const next = [...conditions];
                      next[index] = { ...next[index]!, field: event.target.value };
                      setConditions(next);
                    }}
                  >
                    {trigger.fields.map((field) => (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label={`Bedingung ${index + 1}: Operator`}
                    value={condition.op}
                    onChange={(event) => {
                      const next = [...conditions];
                      next[index] = { ...next[index]!, op: event.target.value as Condition['op'] };
                      setConditions(next);
                    }}
                  >
                    {Object.entries(OP_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  <Input
                    aria-label={`Bedingung ${index + 1}: Wert`}
                    placeholder="Wert"
                    value={condition.value}
                    onChange={(event) => {
                      const next = [...conditions];
                      next[index] = { ...next[index]!, value: event.target.value };
                      setConditions(next);
                    }}
                  />
                  <button
                    type="button"
                    className="auto-condition__remove"
                    aria-label={`Bedingung ${index + 1} entfernen`}
                    onClick={() => setConditions(conditions.filter((_, i) => i !== index))}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() =>
                    setConditions([
                      ...conditions,
                      { field: trigger.fields[0]!.key, op: 'equals', value: '' },
                    ])
                  }
                >
                  + Bedingung
                </Button>
              </div>
            </>
          ) : null}

          {!isSchedule && triggerEvent !== '' ? (
            <Field
              label="Verzögerung"
              hint="0 = sofort. Sonst läuft die Aktion X Tage nach dem Ereignis."
            >
              {(id) => (
                <div className="auto-delay">
                  <Input
                    id={id}
                    type="number"
                    min={0}
                    max={365}
                    value={String(delayDays)}
                    onChange={(event) => setDelayDays(Math.max(0, Number(event.target.value)))}
                  />
                  <span>Tage nach dem Ereignis</span>
                </div>
              )}
            </Field>
          ) : null}

          <div className="auto-section">Dann …</div>
          <Field label="Aktion">
            {(id) => (
              <Select
                id={id}
                required
                value={actionKey}
                onChange={(event) => {
                  setActionKey(event.target.value);
                  setActionParams({});
                }}
              >
                <option value="">– Aktion wählen –</option>
                {(catalog.data?.actions ?? []).map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {(action?.params ?? []).map((param) => (
            <ParamField
              key={`${actionKey}:${param.key}`}
              param={param}
              value={actionParams[param.key] ?? ''}
              onChange={(value) => setActionParams({ ...actionParams, [param.key]: value })}
              placeholderHint={placeholderHint}
            />
          ))}

          <div className="au-dialog-form__actions">
            <Button variant="ghost" onClick={() => setBuilderOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!canSave}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Protokoll */}
      <Dialog
        open={runsFor !== null}
        onClose={() => setRunsFor(null)}
        aria-label="Automations-Protokoll"
      >
        <div className="au-dialog-form">
          <h2>Protokoll: {runsFor?.name}</h2>
          {runs.isLoading ? (
            <SkeletonText lines={4} />
          ) : (runs.data?.items.length ?? 0) === 0 ? (
            <p style={{ color: 'var(--au-text-muted)' }}>
              Noch keine Läufe — die Regel greift beim nächsten passenden Ereignis.
            </p>
          ) : (
            <ul className="auto-runs">
              {runs.data!.items.map((run) => (
                <li key={run.id} className="auto-runs__row">
                  <Badge tone={RUN_BADGE[run.status].tone}>{RUN_BADGE[run.status].label}</Badge>
                  <span className="auto-runs__time">
                    {new Date(run.ranAt).toLocaleString('de-DE', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                  {run.error !== null ? (
                    <span className="auto-runs__error">{run.error}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <div className="au-dialog-form__actions">
            <Button variant="ghost" onClick={() => setRunsFor(null)}>
              Schließen
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
